import { Controller } from "@hotwired/stimulus";

// Drives the console's right-hand "Extract text" panel: a standalone
// scratchpad OCR uploader, independent of any document/candidate — pick or
// drop a file, read its text, copy it out manually. Opened via the header's
// scan-text button. Unlike the left nav drawer, this panel pushes content
// aside rather than overlaying it, so staff can keep it open while clicking
// around the rest of the console (e.g. to paste into a form elsewhere).
export default class extends Controller {
  static targets = ["panel", "dropzone", "input", "status", "textarea"];
  static values = { url: String };

  static DRAG_CLASSES = ["border-primary", "text-primary"];

  toggle() {
    const panel = this.panelTarget;
    panel.dataset.open = panel.dataset.open === "true" ? "false" : "true";
  }

  extract() {
    const file = this.inputTarget.files[0];
    if (file) this.handleFile(file);
  }

  dragOver(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.add(...this.constructor.DRAG_CLASSES);
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove(...this.constructor.DRAG_CLASSES);
  }

  drop(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove(...this.constructor.DRAG_CLASSES);

    const file = event.dataTransfer?.files?.[0];
    if (file) this.handleFile(file);
  }

  async handleFile(file) {
    this.panelTarget.dataset.open = "true";
    this.textareaTarget.value = "";
    this.setStatus("Reading document…");

    const formData = new FormData();
    formData.append("text_extraction", file);

    let response;

    try {
      response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          Accept: "application/json",
        },
        body: formData,
      });
    } catch (error) {
      console.error("Text extraction: upload failed", error);
      this.setStatus("Could not upload the file. Please check your connection and try again.");
      return;
    } finally {
      this.inputTarget.value = "";
    }

    if (!response.ok) {
      console.error("Text extraction: server returned", response.status);
      this.setStatus("Could not read this document. Please try again.");
      return;
    }

    let result;

    try {
      result = await response.json();
    } catch (error) {
      console.error("Text extraction: could not parse response", error);
      this.setStatus("Could not read this document. Please try again.");
      return;
    }

    this.applyResult(result);
  }

  applyResult(result) {
    if (result.status !== "success") {
      this.setStatus(result.message || "Could not read this document. Please try again.");
      return;
    }

    this.textareaTarget.value = result.text;
    this.setStatus("Text extracted — copy and paste as needed.");
  }

  setStatus(message) {
    this.statusTarget.textContent = message;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
